import { useCallback, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { useMutation } from '@apollo/client';

import { useModalRouter } from '@hooks/useModalRouter';
import { getRoute, getCommuneZone } from '@utils';
import { GetCheckoutAddressQueryResponse } from '@queries/checkout/queries/address';
import {
  AssignAddressOrderMutation,
  AssignAddressOrderMutationParams,
  AssignAddressOrderMutationResponse,
} from '@queries/address/mutations';
import { useOrderState } from '@hooks/useOrderState';
import { useSegment } from '@hooks/useSegment';
import { Address } from '@interfaces';

import {
  RootDiv,
  TitleTypography,
  DivisionLine,
  AddressDiv,
  AddressInput,
  AddressTypographyDiv,
  AddressTypographyTitle,
  AddressTypographySubTitle,
  AddAddressDiv,
  AddAddressIconButton,
  AddAddressTypography,
  SkeletonInput,
  SkeletonTitle,
  SkeletonSubTitle,
} from '../styles/Delivery&PickUpContainer.styles';

interface DeliveryContainerProps {
  addresses: GetCheckoutAddressQueryResponse['addresses'];
  currentOrder: GetCheckoutAddressQueryResponse['currentOrder'];
  loadingAddressData: boolean;
  setDeliverySelected: (value: boolean) => void;
  missinInfo: boolean;
  setMissingInfo: (value: boolean) => void;
}

export function DeliveryContainer(props: DeliveryContainerProps) {
  const {
    addresses,
    loadingAddressData,
    setDeliverySelected,
    missinInfo,
    setMissingInfo,
  } = props;
  const router = useModalRouter();
  const addressUuid = router?.query?.addressUuid ?? null;
  const { orderUuid } = useOrderState();
  const { track, debugTrack } = useSegment();

  const [assignAddress] = useMutation<
    AssignAddressOrderMutationResponse,
    AssignAddressOrderMutationParams
  >(AssignAddressOrderMutation());

  const handleAddAddress = () => {
    void track('add_address');
    void router.push(getRoute('checkoutAddAddresses'));
  };

  const handleSelectAddress = async (addressUuid: string) => {
    if (!addresses?.length) {
      debugTrack('checkoutAddress', 'avoid select due missing addresses', {
        addressUuid,
        addresses,
      });
      return;
    }
    setMissingInfo(false);
    setDeliverySelected(true);
    await assignAddress({
      variables: { params: { orderUuid, addressUuid } },
    });
    notifySelectAddress(
      addresses.find((address) => address.uuid === addressUuid),
    );
  };

  const notifySelectAddress = useCallback(
    function handleSelectAddressEvent(address: Address) {
      if (address) {
        void track('address select', {
          commune: address.commune,
          zone: getCommuneZone(address.commune).zoneType,
          deliveryType: 'DELIVERY',
        });
      }
    },
    [track],
  );

  useEffect(() => {
    if (addressUuid) {
      void handleSelectAddress(addressUuid as string);
    }
  }, [addressUuid, addresses]);

  return (
    <RootDiv missingInfo={missinInfo}>
      <TitleTypography>Elige tu dirección de entrega</TitleTypography>
      <DivisionLine />
      {loadingAddressData ? (
        <AddressDiv>
          <SkeletonInput variant="rectangular" width={20} height={20} />
          <AddressTypographyDiv>
            <SkeletonTitle variant="rectangular" width={200} height={20} />
            <SkeletonSubTitle variant="rectangular" width={100} height={20} />
          </AddressTypographyDiv>
        </AddressDiv> /* Address Item */
      ) : (
        addresses?.map((address) => (
          <label key={address.id}>
            <AddressDiv>
              <AddressInput
                type="radio"
                value={address.uuid}
                key={address.id}
                name="address"
                defaultChecked={address.uuid === addressUuid}
                onChange={() => handleSelectAddress(address.uuid)}
              />
              <AddressTypographyDiv>
                <AddressTypographyTitle>
                  {address.street} {address.streetNumber}
                  {address.apartment && ` (${address.apartment})`},{' '}
                  {address.commune}
                </AddressTypographyTitle>
                <AddressTypographySubTitle>
                  {address.name}
                </AddressTypographySubTitle>
              </AddressTypographyDiv>
            </AddressDiv>{' '}
          </label>
        ))
      )}
      <AddAddressDiv onClick={handleAddAddress}>
        <AddAddressIconButton>
          <AddIcon />
        </AddAddressIconButton>
        <AddAddressTypography>Agregar nueva dirección</AddAddressTypography>
      </AddAddressDiv>{' '}
      {/* Address Item */}
    </RootDiv>
  );
}
